import { createAsyncThunk } from "@reduxjs/toolkit";
import type { IHighlight } from "@argument-studio/react-pdf-highlighter-with-categories";
import { createAnnotation, updateCanvasUserData, uploadFile } from "@services/api";
import { highlightToCreateRequest } from "@services/annotationMapper";
import { createReading } from "@features/workspace-readings/state/workspaceReadingsSlice";
import {
  setCanvasData,
  setReadingHighlights,
  setSelectedReading,
} from "@store/slices/workspaceSlice";
import { setBaseView } from "@store/slices/viewSlice";
import type { CanvasData } from "@/types/workspace";
import type { RootState } from "@store/index";

/**
 * Seeds a new account's workspace with sample content so the guided
 * tour has something real to point at — an empty sidebar teaches
 * nothing, and a brand-new user has no readings of their own yet.
 *
 * The fixtures are the *same files demo mode loads* (`public/demo/`),
 * so there is only ever one sample set to keep current. Unlike demo
 * mode, what lands here is ordinary owned content: real rows, real
 * ids, saves persist, and the user can delete it whenever they like —
 * which is why it is labelled "Sample" rather than pretending to be
 * their work.
 */
const HIGHLIGHTS_PATH = "/demo/highlights.json";
const CANVAS_PATH = "/demo/canvas.json";
const PDF_PATH = "/demo/demo.pdf";

export const SAMPLE_READING_NAME =
  "Sample: Theoretical Impediments to Machine Learning";

async function fetchFixture<T>(path: string): Promise<T | null> {
  try {
    const response = await fetch(path);
    if (!response.ok) return null;
    return (await response.json()) as T;
  } catch {
    return null;
  }
}

// Upload the sample PDF as an ordinary document rather than pointing a
// row at the static asset: the API only accepts absolute document_urls
// (and baking an origin into a stored row would break the moment the
// app moves domains). The file is 139 KB and uploads dedupe on content
// hash, so this stays cheap.
async function fetchSamplePdf(): Promise<File | null> {
  try {
    const response = await fetch(PDF_PATH);
    if (!response.ok) return null;
    const blob = await response.blob();
    return new File([blob], "sample.pdf", { type: "application/pdf" });
  } catch {
    return null;
  }
}

export const seedSampleContent = createAsyncThunk<
  { readingId: string } | null,
  void,
  { state: RootState }
>("onboarding/seedSampleContent", async (_arg, { dispatch, getState }) => {
  const state = getState();
  const workspaceId = state.workspace.workspace_id;
  // Every workspace is created with a "Main Canvas" — seed into it
  // rather than adding a second, near-empty canvas.
  const canvasId = state.workspace.canvases[0]?.canvas_id;
  if (!workspaceId) return null;

  const [highlights, canvasData, pdf] = await Promise.all([
    fetchFixture<IHighlight[]>(HIGHLIGHTS_PATH),
    fetchFixture<CanvasData>(CANVAS_PATH),
    fetchSamplePdf(),
  ]);
  if (!highlights || !canvasData || !pdf) return null;

  const upload = await uploadFile(SAMPLE_READING_NAME, pdf);
  if (!upload.success) return null;

  const created = await dispatch(
    createReading({
      workspace_id: workspaceId,
      document_id: upload.data.documentId,
      reading_name: SAMPLE_READING_NAME,
    })
  ).unwrap();
  const readingId = created.reading_id;

  // Annotations are per-user rows; create them sequentially so a
  // partial failure leaves a coherent prefix rather than gaps.
  const persisted: IHighlight[] = [];
  for (const highlight of highlights) {
    const result = await createAnnotation(
      workspaceId,
      readingId,
      highlightToCreateRequest(highlight)
    );
    if (result.success) persisted.push(highlight);
  }
  dispatch(setReadingHighlights({ readingId, highlights: persisted }));

  if (canvasId) {
    const saved = await updateCanvasUserData(workspaceId, canvasId, canvasData);
    if (saved.success) {
      dispatch(setCanvasData({ canvasId, canvasData }));
    }
  }

  // Land on the reading, in reading view — the tour starts there.
  dispatch(setSelectedReading(readingId));
  dispatch(setBaseView("reading"));

  return { readingId };
});
